import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import useOnClickOutside from 'use-onclickoutside';
import { ButtonProps, Input, IconButton } from '@cad-arc';
import { searchIconTestIds } from './test-ids';

export function SearchInput({
    initialValue,
    initialIsOpen = false,
    keepValueOnReinitialize = false,
    onChange = () => {},
    searchButtonVariant = 'ghost',
}: {
    initialValue?: string;
    initialIsOpen?: boolean;
    keepValueOnReinitialize?: boolean;
    onChange?: (value: string) => void;
    searchButtonVariant?: ButtonProps['variant'];
}): React.ReactElement {
    const [value, setValue] = useState(initialValue);
    const [isOpen, setIsOpen] = useState(initialIsOpen);

    const debouncedOnChange = React.useMemo(() => debounce(onChange, 100), [onChange]);

    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const outerRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(outerRef, () => (value ? undefined : setIsOpen(false)));
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);
    useEffect(() => {
        if (!keepValueOnReinitialize) {
            setValue(initialValue);
        }
    }, [initialValue, keepValueOnReinitialize]);

    return (
        <div ref={outerRef}>
            {isOpen ? (
                <Input
                    onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                        e.stopPropagation();
                    }}
                    type="text"
                    ref={inputRef}
                    isClearable
                    leftElement={
                        <IconButton
                            onClick={() => (value ? undefined : setIsOpen(false))}
                            icon="Search"
                            variant="ghost"
                            data-testid={searchIconTestIds.SEARCH_ICON_OPEN}
                            aria-label="open search"
                        />
                    }
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setValue(e.currentTarget.value);
                        debouncedOnChange(e.currentTarget.value);
                    }}
                    role="textbox"
                />
            ) : (
                <IconButton
                    onClick={() => setIsOpen(true)}
                    icon="Search"
                    data-testid={searchIconTestIds.SEARCH_ICON_CLOSED}
                    aria-label="search"
                    variant={searchButtonVariant}
                />
            )}
        </div>
    );
}
